import { useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
} from "@mui/material";
import {
  Description,
  Warning,
  CheckCircle,
  HelpOutline,
} from "@mui/icons-material";
import { Vocabulary } from "../../Utils/Vocabulary";
import styles from "../../Styles/clientAccount.module.css";
import moment from "moment";
import { DocumentData } from "../../Utils/Types";

interface DocumentsSectionProps {
  documents: DocumentData[];
  activeOrders: any[];
  allDocumentTypes: any[];
}

export default function DocumentsSection({
  documents,
  activeOrders,
  allDocumentTypes,
}: DocumentsSectionProps) {
  const isDocumentExpired = (expiryDate: string) => {
    return moment().isAfter(moment(expiryDate));
  };

  const getDocumentStatusText = (expiryDate: string | undefined) => {
    if (!expiryDate) return Vocabulary.missing;
    return isDocumentExpired(expiryDate)
      ? Vocabulary.expired + ` ${moment(expiryDate).format("DD.MM.YYYY")}`
      : `${Vocabulary.validUntil} ${moment(expiryDate).format("DD.MM.YYYY")}`;
  };

  // Collect required document type IDs from all active orders (union, deduplicated)
  const requiredDocTypeIds: string[] = useMemo(() => {
    const ids = new Set<string>();
    for (const order of activeOrders) {
      for (const id of order?.product?.documentTypes || []) {
        ids.add(String(id));
      }
    }
    return Array.from(ids);
  }, [activeOrders]);

  // Resolve IDs to full document type objects
  const requiredDocTypes = useMemo(() => {
    return requiredDocTypeIds
      .map((id) => allDocumentTypes.find((dt) => String(dt._id) === id))
      .filter(Boolean);
  }, [requiredDocTypeIds, allDocumentTypes]);

  // Documents that exist but whose type is NOT in the required list
  const extraDocuments = useMemo(() => {
    return documents.filter((doc) => {
      const docTypeId = String(
        (doc.documentTypeId as any)?._id || doc.documentTypeId || ""
      );
      return docTypeId && !requiredDocTypeIds.includes(docTypeId);
    });
  }, [documents, requiredDocTypeIds]);

  return (
    <Card
      className={styles.infoCard}
      elevation={2}
      style={{ marginBottom: "24px" }}
    >
      <CardContent>
        <Typography variant="h5" className={styles.sectionTitle}>
          <Description className={styles.sectionIcon} />
          {Vocabulary.documentInformation}
        </Typography>

        <Grid container spacing={2} style={{ marginTop: "8px" }}>
          {requiredDocTypes.length === 0 && (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                style={{ textAlign: "center", padding: "16px", color: "#2e3a59" }}
              >
                Nu sunt documente necesare.
              </Typography>
            </Grid>
          )}

          {requiredDocTypes.map((docType: any) => {
            const doc = documents.find(
              (d) =>
                String((d.documentTypeId as any)?._id || d.documentTypeId) ===
                String(docType._id)
            );
            const isMissing = !doc;
            const isExpired =
              doc?.expirationDate ? isDocumentExpired(doc.expirationDate) : false;

            return (
              <Grid item xs={12} sm={6} md={4} key={docType._id}>
                <Card className={styles.documentCard} variant="outlined">
                  <CardContent className={styles.documentContent}>
                    <Box className={styles.documentHeader}>
                      <Typography variant="body1" className={styles.documentName}>
                        {docType.name}
                      </Typography>
                    </Box>
                    <Box className={styles.documentStatus}>
                      {isMissing ? (
                        <HelpOutline className={styles.expiredIcon} />
                      ) : isExpired ? (
                        <Warning className={styles.expiredIcon} />
                      ) : (
                        <CheckCircle className={styles.validIcon} />
                      )}
                      <Typography
                        variant="body2"
                        className={
                          isMissing || isExpired
                            ? styles.expiredText
                            : styles.validText
                        }
                      >
                        {getDocumentStatusText(doc?.expirationDate)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {extraDocuments.map((doc) => {
            const docTypeName =
              (doc.documentTypeId as any)?.name || "Document";
            const isExpired = doc.expirationDate
              ? isDocumentExpired(doc.expirationDate)
              : false;

            return (
              <Grid item xs={12} sm={6} md={4} key={doc._id}>
                <Card className={styles.documentCard} variant="outlined">
                  <CardContent className={styles.documentContent}>
                    <Box className={styles.documentHeader}>
                      <Typography variant="body1" className={styles.documentName}>
                        {docTypeName}
                      </Typography>
                    </Box>
                    <Box className={styles.documentStatus}>
                      {isExpired ? (
                        <Warning className={styles.expiredIcon} />
                      ) : (
                        <CheckCircle className={styles.validIcon} />
                      )}
                      <Typography
                        variant="body2"
                        className={isExpired ? styles.expiredText : styles.validText}
                      >
                        {getDocumentStatusText(doc.expirationDate)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>

    </Card>
  );
}
